import React, { useState } from "react";
import { fetchQuery, QueryRenderer } from "react-relay";

import cx from "classnames";
import moment from "moment";
import { graphql } from "relay-runtime";

import { DueDate } from "~/common";
import { MondayActionItemIntegrationLinkWidget, mondaySpecificDataNodeTypename } from "~/integrations/monday/Widget";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import { Input } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { getIntegrationForKey } from "../actionItemIntegration";
import type { CreateOrImportProps } from "../actionItemIntegration/actionItemLinkingModal";
import { ColumnsSelectors } from "./selectors/MondayColumnsSelectors";

import type { MondayImportExistingTicketPaneMutation } from "~/__generated__/MondayImportExistingTicketPaneMutation.graphql";
import type {
	MondayImportExistingTicketPanePreviewQuery,
	MondayImportExistingTicketPanePreviewQuery$data,
} from "~/__generated__/MondayImportExistingTicketPanePreviewQuery.graphql";
import type { MondayImportExistingTicketPaneTicketQuery } from "~/__generated__/MondayImportExistingTicketPaneTicketQuery.graphql";

import commonStyles from "~/integrations/actionItemIntegration/css/styles.module.css";

import mondayModal from "./css/mondayModal.module.css";

const fetchItemQuery = graphql`
	query MondayImportExistingTicketPaneTicketQuery($itemId: String!) {
		viewer {
			integrations {
				monday {
					item(itemId: $itemId) {
						id
						name
						externalId
						boardId
						boardName
						groupName
					}
				}
			}
		}
	}
`;

const previewQuery = graphql`
	query MondayImportExistingTicketPanePreviewQuery(
		$itemId: String!
		$assigneeColumnId: String
		$dueDateColumnId: String
		$statusColumnId: String
	) {
		viewer {
			integrations {
				monday {
					itemPreview(
						itemId: $itemId
						assigneeColumnId: $assigneeColumnId
						dueDateColumnId: $dueDateColumnId
						statusColumnId: $statusColumnId
					) {
						id
						name
						externalId
						due
						done
						assignee {
							id
							fullName
							image
						}
					}
				}
			}
		}
	}
`;

type ItemType = NonNullable<
	MondayImportExistingTicketPaneTicketQuery["response"]["viewer"]["integrations"]["monday"]
>["item"];

function getItemById(itemId: string) {
	return fetchQuery<MondayImportExistingTicketPaneTicketQuery>(environment, fetchItemQuery, { itemId }).toPromise();
}

const importExternalTicket = graphql`
	mutation MondayImportExistingTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$initialText: String!
		$destinationInfo: JSON!
	) {
		loadActionItemFromIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				initialText: $initialText
				destinationInfo: $destinationInfo
				integrationProvider: MONDAY
			}
		) {
			clientMutationId
		}
	}
`;

function importActionItem(
	blockKey: string,
	noteId: string,
	itemId: string,
	initialText: string,
	assigneeColumnId?: string,
	statusColumnId?: string,
	dueDateColumnId?: string,
) {
	return commitMutationPromise<MondayImportExistingTicketPaneMutation>(environment, {
		mutation: importExternalTicket,
		variables: {
			blockKey,
			noteId,
			destinationInfo: {
				/* eslint-disable camelcase */
				item_id: itemId,
				assignee_column_id: assigneeColumnId,
				status_column_id: statusColumnId,
				due_date_column_id: dueDateColumnId,
				/* eslint-enable camelcase */
			},
			initialText,
		},
	});
}

function getIdFromPastedInput(itemInput: string) {
	// Item URL format: https://fellow-dev.monday.com/boards/2057722627/pulses/2057722661
	const itemIdRegex = /https?:\/\/.*\/pulses\/(\d+)/gi;
	const matches = itemIdRegex.exec(itemInput);
	if (matches && matches?.length > 1) return matches[1];
	return "";
}

export function ImportExistingTicketPane({
	setCanSubmit,
	registerSubmit,
	blockKey,
	noteId,
	initialText,
}: CreateOrImportProps) {
	const [itemId, setItemId] = useState<string>("");
	const [item, setItem] = useState<ItemType>();
	const [lastErroredId, setLastErroredId] = useState<string>();
	const [assigneeColumnId, setAssigneeColumnId] = useState<string>();
	const [statusColumnId, setStatusColumnId] = useState<string>();
	const [dueDateColumnId, setDueDateColumnId] = useState<string>();

	const debouncedItemId = useDebouncedValue(itemId, 300);

	const integration = getIntegrationForKey("MONDAY");
	const { displayName, externalActionItemName } = integration;

	React.useEffect(() => {
		if (debouncedItemId === "") return;

		getItemById(debouncedItemId).then(result => {
			if (result) {
				const {
					viewer: {
						integrations: { monday },
					},
				} = result;

				if (monday) {
					const { item } = monday;

					if (item) {
						setItem(item);
						return;
					}
				}
			}
			setLastErroredId(debouncedItemId);
		});
	}, [debouncedItemId]);

	const onTitleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const itemIdFromURL = getIdFromPastedInput(e.target.value);
		setItemId(itemIdFromURL);
	}, []);

	const submit = React.useCallback(async () => {
		if (!item) {
			throw new Error("Invalid state, submit should not be available if item is null");
		}
		return importActionItem(
			blockKey,
			noteId,
			item.externalId,
			initialText,
			assigneeColumnId,
			statusColumnId,
			dueDateColumnId,
		).then(() => true);
	}, [blockKey, noteId, item, initialText, assigneeColumnId, statusColumnId, dueDateColumnId]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		// check state is valid to be submitted on state change.
		setCanSubmit(!!item && itemId === item.externalId);
	}, [setCanSubmit, item, itemId]);

	const errored = lastErroredId === itemId;

	const renderPreview = React.useCallback(
		({ props }: { props: MondayImportExistingTicketPanePreviewQuery$data | null }) => {
			if (!props) {
				return (
					<div className={mondayModal.spinnerBody}>
						<LoadingSpinner dimmer={false} />
					</div>
				);
			}

			const previewItem = props.viewer.integrations.monday?.itemPreview;
			if (!previewItem || !item) {
				return null;
			}

			return (
				<div>
					<div className={mondayModal.label}>Preview</div>

					<div className={commonStyles.preview}>
						<div className={commonStyles.row}>
							<div className={commonStyles.items}>
								<div className={commonStyles.item}>
									<div className={commonStyles.actionCheckbox} />
								</div>

								<div className={commonStyles.item}>{previewItem.name}</div>
							</div>

							{previewItem.assignee || previewItem.due ? (
								<div className={commonStyles.items}>
									{previewItem.due && (
										<div className={commonStyles.item}>
											<DueDate as="span" styled short date={moment(previewItem.due).format("YYYY-MM-DD")} />
										</div>
									)}

									{previewItem.assignee && (
										<div className={commonStyles.item}>
											<Avatar user={previewItem.assignee} link={false} width="24px" />
										</div>
									)}
								</div>
							) : null}
						</div>

						<div className={commonStyles.pillWrapper}>
							<MondayActionItemIntegrationLinkWidget
								actionItemLink={{
									integrationData: {
										__typename: mondaySpecificDataNodeTypename,
										externalTaskId: previewItem.externalId || "",
										externalGroupName: item.groupName,
										externalBoardId: item.groupName,
										externalBoardName: item.boardName,
										status: "",
										mondayUser: {
											accountSlug: "",
										},
									},
									syncStatus: "DONE_SYNCING_EXTERNALLY",
								}}
							/>
						</div>
					</div>
				</div>
			);
		},
		[item],
	);

	return (
		<div>
			<label className={mondayModal.title}>
				<div className={mondayModal.label}>
					{displayName} {externalActionItemName} URL
				</div>
				<Input
					name="monday_item_url"
					autoFocus
					className={cx(mondayModal.mondayInput, { [mondayModal.mondayInputErrored]: errored })}
					type="text"
					placeholder={`Paste the ${externalActionItemName} URL here`}
					value={itemId}
					onChange={onTitleChange}
				/>
				<p className={cx(mondayModal.labelHelpText, { [mondayModal.labelHelpTextErrored]: errored })}>
					Copy the {externalActionItemName} URL from your browser or by right clicking in {displayName} and selecting
					<b> Copy {externalActionItemName} link</b>
				</p>
			</label>
			<br />
			{item ? (
				<>
					<div className={mondayModal.thirdColumnsRow}>
						<ColumnsSelectors
							boardId={item.boardId}
							assigneeColumnId={assigneeColumnId}
							dueDateColumnId={dueDateColumnId}
							statusColumnId={statusColumnId}
							setAssigneeColumnId={setAssigneeColumnId}
							setDueDateColumnId={setDueDateColumnId}
							setStatusColumnId={setStatusColumnId}
							showLabels={true}
						/>
					</div>

					<div className={mondayModal.previewWrapper}>
						<QueryRenderer<MondayImportExistingTicketPanePreviewQuery>
							environment={environment}
							query={previewQuery}
							variables={{
								itemId: item.externalId,
								assigneeColumnId,
								dueDateColumnId,
								statusColumnId,
							}}
							render={renderPreview}
						/>
					</div>
				</>
			) : itemId ? (
				<div className={mondayModal.spinnerBody}>
					<LoadingSpinner dimmer={false} />
				</div>
			) : (
				<></>
			)}
		</div>
	);
}
